<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="btn-voltar-series"
            color="primary"
            text
            x-small
            :disabled="submittingForm"
            @click="handleBackNavigation"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Etapa
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form :disabled="submittingForm" @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="12">
            <v-card class="pa-4 mb-4" elevation="1">
              <v-card-title class="text-subtitle-1 font-weight-bold pa-0 mb-4">
                Informações Básicas:
              </v-card-title>

              <v-row>
                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Descrição" rules="required" v-slot="{ errors }">
                    <e-label class="required"> Descrição </e-label>

                    <v-text-field
                      id="input-descricao"
                      :disabled="editing"
                      v-model="form.descricao"
                      placeholder="Digite a descrição"
                      dense
                      outlined
                      hide-details="auto"
                      :error-messages="errors"
                      @change="(value) => formatDescription(value)"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="4">
                  <ValidationProvider name="Curso" rules="required" v-slot="{ errors }">
                    <e-label>Curso</e-label>

                    <e-autocomplete
                      id="autocomplete-curso"
                      :disabled="editing"
                      v-model="form.curso_id"
                      :items="cursosList"
                      :loading="isLoadingCursos"
                      :key="cursosList.length"
                      :error-messages="errors"
                      placeholder="Selecione um curso"
                      dense
                      outlined
                      hide-details="auto"
                      item-text="descricao"
                      item-value="id"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="pa-4" elevation="1">
              <v-card-title class="text-subtitle-1 font-weight-bold pa-0 mb-4">
                Componentes Curriculares:
                <v-chip class="ml-2" color="primary">
                  {{ selectedDisciplinesCount }} selecionadas
                </v-chip>
              </v-card-title>

              <e-label class="mb-3">
                Selecione os Componentes Curriculares e defina a carga horária:
              </e-label>

              <v-row v-if="isLoadingDisciplinas">
                <v-col cols="12">
                  <v-progress-linear indeterminate />
                </v-col>
              </v-row>

              <v-row align="start" v-else>
                <v-col
                  v-for="disciplina in disciplinasList"
                  :key="disciplina.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card
                    class="pa-3 disciplina-card"
                    :class="{
                      'selected-disciplina': isDisciplineSelected(disciplina.id),
                    }"
                    elevation="1"
                    outlined
                  >
                    <div class="d-flex align-start">
                      <v-checkbox
                        :id="`checkbox-disciplina-${disciplina.id}`"
                        v-model="form.disciplinaIdList"
                        :label="disciplina.descricao"
                        :value="disciplina.id"
                        hide-details
                        class="mt-0 flex-grow-1"
                      />
                    </div>

                    <v-expand-transition>
                      <div v-if="isDisciplineSelected(disciplina.id)" class="mt-3">
                        <ValidationProvider
                          :name="`Carga Horária`"
                          :rules="{
                            required:
                              isDisciplineSelected(disciplina.id) &&
                              !complementarMap[disciplina.id],
                          }"
                          v-slot="{ errors, validate }"
                        >
                          <div>
                            <e-label class="required"> Carga Horária </e-label>

                            <v-text-field
                              v-model="workloadMap[disciplina.id]"
                              :disabled="complementarMap[disciplina.id]"
                              :error-messages="errors"
                              type="text"
                              inputmode="numeric"
                              min="0"
                              dense
                              outlined
                              class="workload-field"
                              @keypress="onlyNumbers"
                              @paste="handlePaste($event, disciplina.id)"
                              @input="
                                handleNumericInput(disciplina.id);
                                validate(workloadMap[disciplina.id]);
                              "
                              @blur="validate(workloadMap[disciplina.id])"
                            />
                          </div>
                        </ValidationProvider>

                        <ValidationProvider name="Complementar" v-slot="{ errors }">
                          <div class="d-flex align-center">
                            <v-switch
                              id="switch-complementar"
                              v-model="complementarMap[disciplina.id]"
                              @change="handleComplementarChange(disciplina.id)"
                              inset
                              :error-messages="errors"
                              label="Complementar"
                              class="ma-0 pa-0"
                              hide-details
                            />

                            <v-tooltip right max-width="300">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  id="icon-info-historico"
                                  v-bind="attrs"
                                  v-on="on"
                                  color="grey"
                                  size="20"
                                  class="ml-2"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span class="text-caption">
                                Ao ativar a opção 'Complementar' a carga horária do componente
                                curricular complementar será zero e o componente curricular não
                                aparecerá nos relatórios e dashboards.
                              </span>
                            </v-tooltip>
                          </div>
                        </ValidationProvider>
                      </div>
                    </v-expand-transition>
                  </v-card>
                </v-col>
              </v-row>

              <div v-if="disciplinasList.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey lighten-1" class="mb-2"> mdi-book-off </v-icon>
                <div class="text-body-1 grey--text">Nenhum Componente Currícular disponível</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" class="mt-6">
            <v-card class="pa-4" color="grey lighten-4" elevation="0">
              <v-row>
                <v-col cols="12" md="6">
                  <v-btn
                    id="btn-limpar-dados"
                    color="error"
                    outlined
                    block
                    :disabled="submittingForm"
                    @click="resetForm"
                  >
                    <v-icon left> mdi-delete </v-icon>
                    Limpar Dados
                  </v-btn>
                </v-col>

                <v-col cols="12" md="6" class="d-flex">
                  <v-btn
                    id="btn-enviar-dados"
                    color="primary"
                    type="submit"
                    block
                    :loading="submittingForm"
                    :disabled="submittingForm"
                  >
                    <v-icon left> mdi-check </v-icon>
                    {{ submitButtonText }} Etapa
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

export default {
  name: "AnoForm",

  components: { ELabel },

  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cargaHorariaMap: {},
      carregandoCursos: false,
      complementarMap: {},
      cursosList: [],
      form: {
        complementar: false,
        curso_id: null,
        descricao: "",
        disciplinaIdList: [],
      },
      initialized: false,
      submittingForm: false,
    };
  },

  computed: {
    ...mapGetters("Disciplinas", ["disciplinasList", "carregandoDisciplinas"]),
    ...mapGetters("Series", ["series"]),

    isLoadingCursos() {
      return this.carregandoCursos;
    },

    isLoadingDisciplinas() {
      return this.carregandoDisciplinas;
    },

    pageTitle() {
      return this.editing ? "Editando" : "Criando";
    },

    selectedDisciplinesCount() {
      return this.form.disciplinaIdList.length;
    },

    submitButtonText() {
      return this.editing ? "Atualizar" : "Criar";
    },

    workloadMap: {
      get() {
        return this.cargaHorariaMap;
      },
      set(value) {
        this.cargaHorariaMap = value;
      },
    },
  },

  watch: {
    complementarMap: {
      handler(map) {
        Object.keys(map).forEach((id) => {
          if (map[id]) {
            this.$set(this.cargaHorariaMap, id, 0);
          }
        });
      },
      deep: true,
    },

    series: {
      handler() {
        this.tryInitializeForm();
      },
      deep: true,
    },
  },

  async mounted() {
    await this.initializePage();
  },

  methods: {
    ...mapActions("Series", ["loadSeries"]),
    ...mapActions("Disciplinas", ["loadDisciplinas"]),

    /**
     * Formata a descrição para minúsculas com primeira letra minúscula
     * --
     * @param {string} value - Valor a ser formatado
     */
    formatDescription(value) {
      if (!value) return;

      const formatted = value
        .replace(/\s{2,}/g, " ")
        .trim()
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toLowerCase() + word.slice(1))
        .join(" ");

      this.form.descricao = formatted;
    },

    /**
     * Navega de volta para a lista de séries
     */
    handleBackNavigation() {
      this.$router.push({ name: "series" });
    },

    /**
     * Trata valor do input ao ser marcado o switch
     */
    handleComplementarChange(id) {
      if (this.complementarMap[id]) {
        this.$set(this.cargaHorariaMap, id, "0");
      }
    },

    /**
     * Trata o evento de colar na carga horária, permitindo apenas números
     */
    handlePaste(event, id) {
      const paste = (event.clipboardData || window.clipboardData).getData("text");
      const onlyNumbers = paste.replace(/\D/g, "");

      event.preventDefault();
      this.$set(this.workloadMap, id, onlyNumbers);
    },

    /**
     * Impede inserção de caracteres não numéricos na carga horária
     * --
     * @param {number} id - ID da disciplina
     */
    handleNumericInput(id) {
      let value = this.workloadMap[id];

      if (value === null || value === undefined) return;
      value = value.toString().replace(/\D/g, "");

      this.$set(this.workloadMap, id, value);
    },

    onlyNumbers(event) {
      const char = String.fromCharCode(event.keyCode);
      if (!/[0-9]/.test(char)) {
        event.preventDefault();
      }
    },

    /**
     * Inicializa a página carregando todos os dados necessários
     */
    async initializePage() {
      this.$loaderService.open("Carregando dados");

      try {
        await Promise.all([this.loadCursos(), this.loadSeries(), this.loadDisciplinas()]);
        this.tryInitializeForm();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    /**
     * Verifica se uma disciplina está selecionada
     * --
     * @param {number} disciplineId - ID da disciplina
     * @returns {boolean} - True se selecionada
     */
    isDisciplineSelected(disciplineId) {
      return this.form.disciplinaIdList.includes(disciplineId);
    },

    /**
     * Carrega a lista de cursos
     */
    async loadCursos() {
      this.carregandoCursos = true;

      try {
        const cursos = await this.$services.cursosService.syncAll();
        this.cursosList = cursos;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoCursos = false;
      }
    },

    /**
     * Reseta o formulário para o estado inicial
     */
    resetForm() {
      this.form.disciplinaIdList = [];
      this.cargaHorariaMap = {};
      this.complementarMap = {};
    },

    /**
     * Envia o formulário para criação ou atualização
     */
    async submitForm() {
      const hasInvalidWorkload = this.form.disciplinaIdList.some((id) => {
        return (
          !this.complementarMap[id] &&
          (this.cargaHorariaMap[id] === undefined ||
            this.cargaHorariaMap[id] === null ||
            this.cargaHorariaMap[id] === "")
        );
      });
      if (hasInvalidWorkload) {
        this.$toast.danger(
          "Preencha a carga horária de todos os componentes curriculares selecionados."
        );
        return;
      }

      this.submittingForm = true;

      const payload = this.buildPayload();

      try {
        if (this.editing) {
          await this.$services.seriesService.atualizarSerie(payload);
          this.$toast.success("Etapa atualizada com sucesso");
        } else {
          await this.$services.seriesService.criarSerie(payload);
          this.$toast.success("Etapa criada com sucesso");
        }

        this.$router.push({ name: "series" });
      } catch (error) {
        this.handleSubmitError(error);
      } finally {
        this.submittingForm = false;
      }
    },

    /**
     * Constrói o payload para envio
     * --
     * @returns {Object} Payload formatado
     */
    buildPayload() {
      const { serie_id } = this.$route.params;

      const payload = {
        curso_id: this.form.curso_id,
        descricao: this.form.descricao,
        disciplinas: this.form.disciplinaIdList.map((id) => ({
          id,
          carga_horaria: this.complementarMap[id] ? 0 : Number(this.cargaHorariaMap[id] || 0),
          disciplina_extra_curricular: !!this.complementarMap[id],
        })),
      };
      if (this.editing) {
        payload.id = parseInt(serie_id, 10);
      }
      return payload;
    },

    /**
     * Trata erros do submit
     * --
     * @param {Error} error - Objeto de erro
     */
    handleSubmitError(error) {
      const errorMessage = error.message || error.error?.message || "Erro ao salvar";
      this.$toast.danger(errorMessage);
    },

    /**
     * Tenta inicializar o formulário no modo de edição
     */
    tryInitializeForm() {
      if (!this.editing || this.initialized) return;
      if (!this.series || this.series.length === 0) return;

      const { serie_id } = this.$route.params;
      const serie = this.series.get(parseInt(serie_id, 10));

      if (!serie) return;

      this.initializeFormWithSerieData(serie);
      this.initialized = true;
    },

    /**
     * Inicializa o formulário com dados da série
     * --
     * @param {Object} serie - Dados da série
     */
    initializeFormWithSerieData(serie) {
      this.form = {
        descricao: serie.descricao,
        curso_id: serie.curso_id,
        disciplinaIdList: (serie.disciplinas || []).map((d) => d.id),
      };

      this.cargaHorariaMap = {};

      (serie.disciplinas || []).forEach((disciplina) => {
        const workload = disciplina?.pivot?.carga_horaria;

        this.$set(this.cargaHorariaMap, disciplina.id, this.normalizeWorkload(workload));
      });

      this.complementarMap = {};

      (serie.disciplinas || []).forEach((disciplina) => {
        const complementar = disciplina?.pivot?.disciplina_extra_curricular;

        this.$set(this.complementarMap, disciplina.id, !!complementar);
      });
    },

    /**
     * Normaliza o valor da carga horária
     * --
     * @param {any} workload - Valor da carga horária
     * @returns {number} Valor normalizado
     */
    normalizeWorkload(workload) {
      return workload !== null && workload !== undefined ? Number(workload) : 0;
    },
  },
};
</script>

<style scoped>
.disciplina-card {
  display: block;
  height: auto;
}

.disciplina-card .v-input--checkbox {
  align-items: flex-start;
}

.disciplina-card .v-input--selection-controls__input {
  margin-top: 2px;
}

.disciplina-card .v-expand-transition {
  margin-top: auto;
}

.required::after {
  content: " *";
  color: red;
}

.selected-disciplina {
  border: 2px solid #2196f3;
  background-color: #e3f2fd;
  transition: all 0.3s ease;
}

.workload-field {
  max-width: 120px;
}

.v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

@media (hover: hover) {
  .selected-disciplina:hover {
    background-color: #bbdef5;
  }
}

@media (max-width: 600px) {
  .workload-field {
    max-width: 100%;
  }
}
</style>
